import axios, {parseResponseData} from '@/axiosInstance.ts'
import {z} from 'zod'
import {
    MedicalReportAttributeKey,
    MedicalReportSmall,
    MedicalReportTypeByProject,
    MedicalReportDateByProject,
    MedicalReportExtended
} from '@/features/medical-reports/types'
import {
    MedicalReportGroup,
    MedicalReportType,
    MedicalReportTypeKeyName,
    PaginationParams,
    PaginationResponse,
    ResponseType
} from '@/types'
import {captureException} from '@sentry/react'

// get medical reports
export type HttpGetMedicalReportsOptions = {
    search?: string
    project?: string
    responseType?: ResponseType
} & PaginationParams

export const httpGetMedicalReports = (params: HttpGetMedicalReportsOptions) =>
    axios.get('medical-record-reports', {params})

// get medical reports by project
export type HttpGetMedicalReportsByPatientOptions = {
    urlParams: {
        patientId: string
    }
    params: PaginationParams & {
        startDate?: string
        endDate?: string
        search?: string
        createdByUserId?: string
        group?: MedicalReportGroup
        projectId?: string
        medicalRecordReportTypeId?: string
        responseType?: ResponseType
    }
}

export const HttpGetMedicalReportsByPatientResponse = z.object({
    data: z.array(MedicalReportSmall),
    meta: PaginationResponse
})
export type HttpGetMedicalReportsByPatientResponse = z.infer<typeof HttpGetMedicalReportsByPatientResponse>

export const httpGetMedicalReportsByPatient = async ({urlParams, params}: HttpGetMedicalReportsByPatientOptions) => {
    const response = await axios.get(`patients/${urlParams.patientId}/medical-record-reports`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetMedicalReportsByPatientResponse
    })
}

// get medical report types
export type HttpGetMedicalReportTypesOptions = {
    search?: string
}
export const httpGetMedicalReportTypes = (params: HttpGetMedicalReportTypesOptions) =>
    axios.get(`medical-record-report-types`, {params})

// get medical report type
export type HttpGetMedicalReportTypeOptions = {
    urlParams: {
        id: string
    }
}
export const httpGetMedicalReportType = async ({urlParams}: HttpGetMedicalReportTypeOptions) => {
    const response = await axios.get(`medical-record-report-types/${urlParams.id}`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: MedicalReportType
    })
}

// get previous medical report
export type HttpGetPreviousReportByProjectOptions = {
    urlParams: {
        projectId: string
    }
    params: {
        medicalRecordReportTypeId: string
        responseType: ResponseType
    }
}
export const httpGetPreviousReportByProject = ({urlParams, params}: HttpGetPreviousReportByProjectOptions) =>
    axios.get(`projects/${urlParams.projectId}/show-medical-record-report`, {params})

// show medical report
export type HttpGetMedicalReportOptions<TResponseType extends ResponseType> = {
    urlParams: {
        medicalReportId: string
    }
    params: {
        responseType: TResponseType
    }
}
const HttpGetMedicalReportResponse = z.object({
    small: MedicalReportSmall,
    extended: MedicalReportExtended
})
export const httpGetMedicalReport = async <TResponseType extends ResponseType>({
    urlParams,
    params
}: HttpGetMedicalReportOptions<TResponseType>) => {
    const response = await axios.get(`medical-record-reports/${urlParams.medicalReportId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetMedicalReportResponse.shape[params.responseType]
    })
}

// create medical report
export const HttpMedicalReportPayload = z.object({
    medicalRecordReportTypeId: z.string(),
    medicalRecordId: z.string(),
    endDate: z.string().nullish(),
    projectId: z.string().nullish(),
    accessIds: z.array(z.string()).nullish(),
    attributes: z
        .array(
            z.object({
                id: z.string().optional(),
                name: MedicalReportAttributeKey,
                value: z.string(),
                object: z.array(z.record(z.string(), z.unknown())).optional()
            })
        )
        .optional(),
    status: MedicalReportSmall.shape['status'],
    date: z.string()
})
export type HttpMedicalReportPayload = z.infer<typeof HttpMedicalReportPayload>

export const httpCreateMedicalReport = (payload: HttpMedicalReportPayload) =>
    axios.post(`medical-record-reports`, payload)

// update medical report
export const HttpUpdateMedicalReportOptions = z.object({
    urlParams: z.object({medicalReportId: z.string()}),
    payload: HttpMedicalReportPayload.partial()
})
export type HttpUpdateMedicalReportOptions = z.infer<typeof HttpUpdateMedicalReportOptions>

export const httpUpdateMedicalReport = ({urlParams, payload}: HttpUpdateMedicalReportOptions) =>
    axios.patch(`medical-record-reports/${urlParams.medicalReportId}`, payload)

// Get medical report types by project
export type HttpGetMedicalReportTypesByPatientOptions = {
    params: PaginationParams & {
        patientId: string
        userId: string
        search?: string
    }
}

const HttpGetMedicalReportTypesByPatientResponse = z.object({
    data: z.array(MedicalReportTypeByProject),
    meta: PaginationResponse
})
export type HttpGetMedicalReportTypesByPatientResponse = z.infer<typeof HttpGetMedicalReportTypesByPatientResponse>

export const httpGetMedicalReportTypesByPatient = async ({params}: HttpGetMedicalReportTypesByPatientOptions) => {
    const response = await axios.get(`/aggregated-prints/medical-record-report-types`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetMedicalReportTypesByPatientResponse
    })
}

// Get medical report dates by project
export type HttpGetReportDatesByPatientOptions = {
    params: PaginationParams & {
        patientId: string
        userId: string
        medicalRecordReportTypeId: string
    }
}

const HttpGetReportDatesByPatientResponse = z.object({
    data: z.array(MedicalReportDateByProject),
    meta: PaginationResponse
})
export type HttpGetReportDatesByPatientResponse = z.infer<typeof HttpGetReportDatesByPatientResponse>

export const httpGetReportDatesByPatient = async ({params}: HttpGetReportDatesByPatientOptions) => {
    const response = await axios.get(`/aggregated-prints/dates`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetReportDatesByPatientResponse
    })
}

// Delete medical report
export type HttpDeleteMedicalReportOptions = {
    urlParams: {
        medicalReportId: string
    }
}
export const httpDeleteMedicalReport = ({urlParams}: HttpDeleteMedicalReportOptions) =>
    axios.delete(`medical-record-reports/${urlParams.medicalReportId}`)

// Generate medical report pdf
export type HttpGenerateMedicalReportPdfOptions = {
    urlParams: {medicalReportId?: string}
}
export const httpGenerateMedicalReportPdf = ({urlParams}: HttpGenerateMedicalReportPdfOptions) =>
    axios.post(`medical-record-reports/${urlParams.medicalReportId}/generate-pdf`)

// Get medical report by project and medical report type enum
export type HttpGetMedicalReportByProjectKeyNameTypeOptions = {
    params: {
        projectId: string
        medicalRecordReportTypeKeyName: MedicalReportTypeKeyName
    }
}
export const httpGetMedicalReportByProjectKeyName = async ({
    params
}: HttpGetMedicalReportByProjectKeyNameTypeOptions) => {
    const response = await axios.get(`medical-record-reports/show-by-project`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: MedicalReportSmall
    })
}
